/** @jsxImportSource @emotion/react */
import { ListItemIcon, ListItemText, Menu, MenuItem, Theme, useTheme } from '@mui/material';
import UserIcon from 'components/icons/UserIcon';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import OpenInBrowserIcon from '@mui/icons-material/OpenInBrowser';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import React, { useState } from 'react';
import { StylesClassName, UseStyles } from 'types/styles';
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { Link } from 'react-router-dom';
import useLogout from 'hooks/useLogout';
import { usePaths } from 'hooks/usePaths';
import { FormattedMessage } from 'react-intl';
import { config } from 'application';

interface IAuthDropdownProps {
  className?: StylesClassName;
}

const AuthDropdown: React.FC<IAuthDropdownProps> = ({ className }) => {
  const theme = useTheme() as Theme;
  const classes = useStyles(theme);
  const paths = usePaths();
  const { logout } = useLogout();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    logout();
  };

  return (
    <div css={[classes.AuthDropdown, className]}>
      <button css={classes.btn} onClick={handleClick}>
        <UserIcon css={[classes.userIcon, open ? classes.hoverUserIcon : '']} className="userIcon" />
        <ArrowDropDownIcon css={open ? classes.arrowIcon : ''} />
      </button>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <AuthenticatedTemplate>
          <MenuItem component={'a'} href={config.partnersLink}>
            <ListItemIcon>
              <OpenInBrowserIcon />
            </ListItemIcon>
            <ListItemText>
              <FormattedMessage id={'AuthDropdown.partners'} defaultMessage={'Partners platform'} />
            </ListItemText>
          </MenuItem>
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText>
              <FormattedMessage id={'common.logout'} defaultMessage={'Logout'} />
            </ListItemText>
          </MenuItem>
        </AuthenticatedTemplate>
        <UnauthenticatedTemplate>
          <MenuItem to={paths.signIn.generate()} component={Link}>
            <ListItemIcon>
              <LoginIcon />
            </ListItemIcon>
            <ListItemText>
              <FormattedMessage id={'common.login'} defaultMessage={'Login'} />
            </ListItemText>
          </MenuItem>
        </UnauthenticatedTemplate>
      </Menu>
    </div>
  );
};

const useStyles = (theme: Theme): UseStyles => ({
  AuthDropdown: {},
  btn: {
    background: 'none',
    border: 0,
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',

    svg: {
      path: {
        fill: theme.palette.common.white,
      },
    },
    '&:hover': {
      '.userIcon': {
        path: {
          fill: theme.palette.custom.primary.brand[20],
        },
      },
    },
  },
  userIcon: {
    width: 40,
    height: 40,
  },
  hoverUserIcon: {
    path: {
      fill: `${theme.palette.custom.primary.brand[20]} !important`,
    },
  },
  arrowIcon: {
    transform: 'rotate(180deg)',
  },
});

export default AuthDropdown;
